import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import { Stack, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { APP_ICONS } from "../../../assets";
import AppLoader from "../../../components/AppLoader";
import PaperContainer from "../../../components/Containers/PaperContainer";
import StatusBlockDialog from "../../../components/Dialogs/StatusBlockDialog";
import DynamicPaginatedTable from "../../../components/Tables/DynamicPaginatedTable";
import TextInput from "../../../components/TextInputs";
import useDebounce from "../../../hooks/useDebounce";
import { setSearchKeys } from "../../../lib/slices/searchKeysSlice";
import {
  getAllCreators,
  updateCreatorAccountStatus,
  updateCreatorOnboardingStatus,
} from "../../../services/creatorApiFn";
import { handleOpenSnackBar } from "../../../utils";
import { updateAllAcountStatus } from "../../../services/commonApiFn";

const headerData = [
  "createdAt",
  "creatorDetails",
  "creatorType",
  "socialLinks",
  "onboardingStatus",
  "accountStatus",
];

const tableRows = [
  "created_date",
  "creator_details",
  "creator_type",
  "creator_social_links",
  "creator_onboarding_status",
  "creator_account_status",
];

function CreatorLists() {
  const { t } = useTranslation(["client", "common"]);
  const dispatch = useDispatch();
  const openRejectDialog = useRef(null);
  const openBlockStatusDialog = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [allCreators, setAllCreators] = useState([]);
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const debouncedSearchTerm = useDebounce(searchKeys.creatorByName, 1000);

  const fetchAllCreators = async (search) => {
    try {
      setIsLoading(true);
      const params = {
        search: search,
        page: 1,
        limit: 500,
      };
      const response = await getAllCreators(params);
      if (response.data.status === "success") {
        const data = response.data.data.creators;
        setAllCreators(data);
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchAllCreators(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  // Onboarding status (pending / approved / rejected)
  const handleChangeOnboardingStatus = (status, row) => {
    if (status === row?.status) return;
    if (status === "rejected") {
      openRejectDialog.current.openDialog({
        type: "Creator",
        status: "Reject",
        data: row,
        event: status,
      });
      return;
    }
    handleUpdateOnboardingStatus({ data: row, event: status });
  };

  const handleUpdateOnboardingStatus = async (params) => {
    try {
      let payload = {
        status: params?.event,
      };
      if (params?.reason) payload.reason = params.reason;
      setIsLoading(true);
      const response = await updateCreatorOnboardingStatus(
        params?.data?._id,
        payload,
      );
      if (response.data.status === "success") {
        fetchAllCreators(searchKeys.creatorByName);
        handleOpenSnackBar("success", response.data.message);
        openRejectDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Account status (isActive)
  const handleOpenConfirmDialog = (e, row) => {
    openBlockStatusDialog.current.openDialog({
      type: "Creator",
      status: e ? "Unblock" : "Block",
      data: row,
      event: e,
    });
  };

  const handleUpdateAccountStatus = async (params) => {
    try {
      let payload = {
        isActive: params?.event,
        reason: params.reason,
      };
      setIsLoading(true);
      // const response = await updateCreatorAccountStatus(
      //   params?.data?._id,
      //   payload,
      // );
      const response = await updateAllAcountStatus(
        params?.data?._id,
        payload,
        "Creator"
      );
      if (response.data.status === "success") {
        fetchAllCreators(searchKeys.creatorByName);
        handleOpenSnackBar("success", response.data.message);
        openBlockStatusDialog.current.closeDialog({});
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const renderSearchAction = () => {
    return (
      <Stack p={2} direction={{ md: "row" }} bgcolor={"primary.light"}>
        <TextInput
          styleProps={{ width: { xs: "100%", md: "35%", xl: "30%" } }}
          InputStartIcon={<img src={APP_ICONS.searchGreyIcon} width={25} />}
          InputEndIcon={
            searchKeys.creatorByName && (
              <CancelOutlinedIcon
                fontSize="small"
                sx={{ cursor: "pointer" }}
                onClick={() => {
                  dispatch(
                    setSearchKeys({
                      ...searchKeys,
                      creatorByName: "",
                    }),
                  );
                }}
              />
            )
          }
          placeholder={t("searchByName", { ns: "common" })}
          value={searchKeys.creatorByName || ""}
          onChange={(e) => {
            dispatch(
              setSearchKeys({
                ...searchKeys,
                creatorByName: e.target.value,
              }),
            );
          }}
        />
      </Stack>
    );
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <PaperContainer
        title={t("creatorLists")}
        subtitle={t("manageCreators")}
        searchActions={renderSearchAction()}
      >
        <DynamicPaginatedTable
          showPagination={true}
          tableHeader={headerData}
          displayRows={tableRows}
          tableData={allCreators}
          handleChangeStatus={handleOpenConfirmDialog}
          handleChangeOnboardingStatus={handleChangeOnboardingStatus}
        />
      </PaperContainer>
      <StatusBlockDialog
        ref={openRejectDialog}
        onConfirm={handleUpdateOnboardingStatus}
      />
      <StatusBlockDialog
        ref={openBlockStatusDialog}
        onConfirm={handleUpdateAccountStatus}
      />
    </>
  );
}

export default CreatorLists;
